import React, { useEffect, useMemo, useState } from "react";
import type { Chart as ChartJS } from "chart.js";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChartBar, Target as TargetIcon } from "@phosphor-icons/react";
import { GoalStatus, Target } from "hooks/api/documentGeneration/useGetDocumentChecklist";
import useGetGoalMasteryHistory from "hooks/api/goals/useGetGoalMasteryHistory";
import useGetTargetSessionMetrics from "hooks/api/targets/useGetTargetSessionMetrics";
import useListTargetMarkers from "hooks/api/targets/useListTargetMarkers";
import GoalCumulativeRecordChart from "../../../Sections/GoalsAndTargets/Goals/GoalCumulativeRecordChart";
import TargetDistributionChart from "../../../Sections/GoalsAndTargets/Targets/TargetDistributionChart";
import TargetMetricChart from "../../../Sections/GoalsAndTargets/Targets/TargetMetricChart";
import TargetStepBreakdownChart from "../../../Sections/GoalsAndTargets/Targets/TargetStepBreakdownChart";
import {
  getMetricChartConfig,
  hasLineMetricValue,
} from "../../../Sections/GoalsAndTargets/Targets/metricChartRegistry";
import { buildDistributionData } from "../../../Sections/GoalsAndTargets/utils/buildDistributionData";
import {
  DISTRIBUTION_SEGMENT_IDS,
  STEP_ACQUISITION_SEGMENT_ID,
  hasDataForChartOption,
} from "../../../Sections/GoalsAndTargets/utils/hasDataForChartOption";
import { buildStepAcquisitionRows } from "../../../Sections/GoalsAndTargets/utils/buildStepAcquisitionRows";
import { derivePhaseZones } from "../../../Sections/GoalsAndTargets/utils/derivePhaseZones";
import { resolveChartMarkers } from "../../../Sections/GoalsAndTargets/utils/resolveChartMarkers";
import { toBannerCriteria } from "../../../Sections/GoalsAndTargets/utils/deriveStatusInsights";
import {
  ChartEmptyState,
  ChartErrorState,
} from "../../../Sections/GoalsAndTargets/shared/ChartStateCard";
import getStatusStyles from "../../../Sections/GoalsAndTargets/shared/StatusPill/getStatusColor";
import { useTheme } from "@mui/material/styles";
import { getAnalyticsToggleOptions } from "../../../Sections/GoalsAndTargets/Targets/TargetBuilderPanel/l-model/analyticsToggleOptions";

interface GoalProgressCardProps {
  /** The goal to render */
  goal: GoalStatus;
  /** Optional right-side italic caption (e.g. "Mastered") */
  caption?: string;
  /** Current progress statement text (owned by the dialog). */
  progressStatement: string;
  /** Called when the progress statement textarea changes. */
  onProgressStatementChange: (value: string) => void;
  /** Per-target chart-image inclusion. Undefined = included (default). */
  selectedTargetIds: Record<string | number, boolean>;
  /** Called when a target's chart-image inclusion is toggled. */
  onTargetSelectionChange: (targetId: string | number, selected: boolean) => void;
  /** Registers a chart instance with the dialog so it can be snapshotted at generate time. */
  registerChart: (
    goalId: string | number,
    kind: "cumulative" | "target",
    targetId: string | number | null,
    chart: ChartJS<"line"> | ChartJS<"bar"> | null
  ) => void;
}

type ChartMode = "cumulative" | "individual";

interface TargetProgressBlockProps {
  target: Target;
  /**
   * The parent goal's behavior subject ("client" | "caregiver"). Not yet a typed
   * field on `GoalStatus` — read defensively via its raw-CR-passthrough index
   * signature so this respects it the moment the document-checklist endpoint
   * (an-api) starts including it, without another FE change.
   */
  behaviorSubject?: string;
  isIncluded: boolean;
  onToggle: (selected: boolean) => void;
  onChartReady: (chart: ChartJS<"line"> | ChartJS<"bar"> | null) => void;
}

function readPromptConfig(step35Config: unknown): {
  hasPromptHierarchy: boolean;
  countPromptedAsCorrect: boolean;
} {
  const config = (step35Config ?? {}) as Record<string, unknown>;
  const rawHierarchy = config.promptHierarchy ?? config.prompt_hierarchy;
  const nested =
    rawHierarchy && typeof rawHierarchy === "object" && !Array.isArray(rawHierarchy)
      ? (rawHierarchy as Record<string, unknown>)
      : null;
  const levels = Array.isArray(rawHierarchy)
    ? rawHierarchy
    : nested && Array.isArray(nested.levels)
      ? nested.levels
      : [];
  const countPromptedAsCorrect =
    config.countPromptedAsCorrect ??
    config.count_prompted_as_correct ??
    nested?.countPromptedAsCorrect ??
    nested?.count_prompted_as_correct;

  return {
    hasPromptHierarchy: levels.length > 0,
    countPromptedAsCorrect: countPromptedAsCorrect === true,
  };
}

type SessionHistoryState = "yes" | "no" | "unknown";

function getGoalSessionHistoryState(targets: Target[]): SessionHistoryState {
  const anyTargetHasSessionKey = targets.some(
    (target) => "sessions" in (target as unknown as Record<string, unknown>)
  );

  if (!anyTargetHasSessionKey) return "unknown";

  const hasAnySessions = targets.some((target) => {
    const targetLike = target as unknown as {
      sessions?: unknown;
      target_sessions?: unknown;
    };
    const sessions = Array.isArray(targetLike.sessions)
      ? targetLike.sessions
      : Array.isArray(targetLike.target_sessions)
        ? targetLike.target_sessions
        : [];
    return sessions.length > 0;
  });

  return hasAnySessions ? "yes" : "no";
}

const TargetProgressBlock: React.FC<TargetProgressBlockProps> = ({
  target,
  behaviorSubject,
  isIncluded,
  onToggle,
  onChartReady,
}) => {
  const { hasPromptHierarchy, countPromptedAsCorrect } = useMemo(
    () => readPromptConfig(target.step35Config),
    [target.step35Config]
  );

  // Chart options derived from the target's L4 metric catalogue (shared with TargetDetail).
  const candidateOptions = useMemo(
    () =>
      getAnalyticsToggleOptions(target.l1Dimension, target.l3Context, {
        hasPromptHierarchy,
        countPromptedAsCorrect,
        behaviorSubject,
      }).filter(
        (option) =>
          option.id === undefined ||
          DISTRIBUTION_SEGMENT_IDS.has(option.id) ||
          option.id === STEP_ACQUISITION_SEGMENT_ID
      ),
    [
      target.l1Dimension,
      target.l3Context,
      hasPromptHierarchy,
      countPromptedAsCorrect,
      behaviorSubject,
    ]
  );

  const sessionMetricsTargetId = isIncluded && candidateOptions.length > 0 ? Number(target.id) : 0;
  const {
    sessionMetrics,
    stepAcquisitionSequence,
    isLoadingSessionMetrics,
    sessionMetricsError,
    refetch,
  } = useGetTargetSessionMetrics(sessionMetricsTargetId);
  const { markers } = useListTargetMarkers(sessionMetricsTargetId);

  // Only offer metrics this target has recorded data for — a candidate whose
  // backing field is null across all sessions would chart blank. Shared with
  // TargetDetail so both surfaces agree on what counts as "has data".
  const chartOptions = useMemo(
    () =>
      candidateOptions.filter((option) =>
        hasDataForChartOption(option, { sessionMetrics, stepAcquisitionSequence })
      ),
    [candidateOptions, sessionMetrics, stepAcquisitionSequence]
  );

  const [selectedChart, setSelectedChart] = useState<string | null>(null);

  // Reset the selection when the available options change (e.g. L config edited upstream).
  useEffect(() => {
    if (!selectedChart) return;
    const stillValid = chartOptions.some((option) => option.value === selectedChart);
    if (!stillValid) setSelectedChart(null);
  }, [chartOptions, selectedChart]);

  const activeChart = selectedChart ?? chartOptions[0]?.value ?? null;
  const activeOption = chartOptions.find((o) => o.value === activeChart);
  const activeChartConfig = useMemo(() => {
    const config = getMetricChartConfig(activeOption?.label);
    return config && config.kind !== "distribution" ? config : null;
  }, [activeOption?.label]);
  const distributionData = useMemo(
    () =>
      activeOption?.id !== undefined
        ? buildDistributionData(activeOption.id, sessionMetrics)
        : null,
    [activeOption?.id, sessionMetrics]
  );
  const stepAcquisitionRows = useMemo(
    () =>
      activeOption?.id === STEP_ACQUISITION_SEGMENT_ID
        ? buildStepAcquisitionRows(stepAcquisitionSequence, Math.max(1, sessionMetrics.length))
        : null,
    [activeOption?.id, stepAcquisitionSequence, sessionMetrics.length]
  );
  const lineChartMetrics = useMemo(() => {
    if (!activeChartConfig) return sessionMetrics;
    return sessionMetrics.filter((metric) => hasLineMetricValue(metric, activeChartConfig));
  }, [activeChartConfig, sessionMetrics]);

  const masteryCriterion = useMemo(() => {
    // Checklist targets arrive deep-camelCased; map back to the snake_case keys toBannerCriteria reads.
    const raw = (target.masteryCriteria ?? {}) as Record<string, unknown>;
    const criteria = toBannerCriteria(
      {
        threshold: raw.threshold,
        threshold_unit: raw.threshold_unit ?? raw.thresholdUnit,
        percent_threshold: raw.percent_threshold ?? raw.percentThreshold,
        value_threshold: raw.value_threshold ?? raw.valueThreshold,
        occurrence_threshold: raw.occurrence_threshold ?? raw.occurrenceThreshold,
        percent: raw.percent,
        consecutive_data_points: raw.consecutive_data_points ?? raw.consecutiveDataPoints,
        consecutive_points: raw.consecutive_points ?? raw.consecutivePoints,
      },
      target.l1Dimension
    );
    // Only a percent threshold belongs on a percentage axis — a duration/latency
    // criterion is in seconds and would plot as a bogus percentage.
    return criteria?.threshold_unit === "percent" ? criteria.threshold : undefined;
  }, [target.masteryCriteria, target.l1Dimension]);
  const phaseZones = useMemo(() => derivePhaseZones(lineChartMetrics), [lineChartMetrics]);
  const markerLines = useMemo(
    () => resolveChartMarkers(markers ?? [], lineChartMetrics),
    [markers, lineChartMetrics]
  );

  return (
    <Stack sx={{ gap: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ gap: 3, minHeight: 40 }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={isIncluded}
              onChange={(e) => onToggle(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" fontWeight={500}>
              {target.title}
            </Typography>
          }
        />
        {isIncluded && chartOptions.length > 0 && activeChart && (
          <FormControl size="small" sx={{ width: 220, flexShrink: 0 }}>
            <Select value={activeChart} onChange={(e) => setSelectedChart(e.target.value)}>
              {chartOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>
      {isIncluded && (
        <>
          {isLoadingSessionMetrics ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
              <CircularProgress aria-label="Loading session metrics" />
            </Box>
          ) : sessionMetricsError ? (
            <ChartErrorState minHeight={320} onRetry={() => refetch()} />
          ) : stepAcquisitionRows && activeOption ? (
            <TargetStepBreakdownChart
              title={activeOption.label}
              rows={stepAcquisitionRows}
              totalSessions={Math.max(1, sessionMetrics.length)}
              showCanvasLegend
              onChartReady={onChartReady}
            />
          ) : distributionData && activeOption ? (
            <TargetDistributionChart
              title={activeOption.label}
              data={distributionData}
              xAxisLabel={
                activeOption.id === "step-level-errors"
                  ? "Error Count"
                  : activeOption.id === "latency-by-prompt-level"
                    ? "Latency (s)"
                    : "Count"
              }
              onChartReady={onChartReady}
            />
          ) : !activeChartConfig || lineChartMetrics.length === 0 ? (
            <ChartEmptyState minHeight={320} />
          ) : (
            <TargetMetricChart
              config={activeChartConfig}
              metrics={lineChartMetrics}
              masteryCriterion={masteryCriterion}
              phaseZones={phaseZones}
              markers={markerLines}
              onChartReady={onChartReady}
            />
          )}
        </>
      )}
    </Stack>
  );
};

/**
 * Card-style row used in the Reassessment Clinical Goals step.
 * Shows the goal title/description/caption, a Progress Visualization toggle
 * (Cumulative Record / Individual Target Progress) with the corresponding
 * chart, and a Progress Statement textarea (in-progress and mastered goals
 * only).
 */
const GoalProgressCard: React.FC<GoalProgressCardProps> = ({
  goal,
  caption,
  progressStatement,
  onProgressStatementChange,
  selectedTargetIds,
  onTargetSelectionChange,
  registerChart,
}) => {
  const showProgressStatement = goal.status === "in-progress" || goal.status === "met";

  const targets: Target[] = goal.targets ?? [];
  const showChart = goal.status !== "new";

  const [chartMode, setChartMode] = useState<ChartMode>("cumulative");

  const totalTargets = targets.length;

  const masteryHistoryGoalId = showChart && targets.length > 0 ? Number(goal.id) : 0;
  const {
    series: masteryHistorySeries,
    isMasteryHistoryLoading,
    errorMasteryHistory,
    refetch: refetchMasteryHistory,
  } = useGetGoalMasteryHistory(masteryHistoryGoalId);
  const cumulativeLabels = masteryHistorySeries.map((point) => `W${point.week}`);
  const cumulativeData = masteryHistorySeries.map((point) => point.targets_mastered);
  const sessionHistoryState = useMemo(() => getGoalSessionHistoryState(targets), [targets]);
  // Never clip a cumulative count that outruns the current target total
  // (e.g. a mastered target later archived).
  const masteredCount = cumulativeData.length ? cumulativeData[cumulativeData.length - 1] : 0;
  const totalTargetsForChart = Math.max(totalTargets, masteredCount, 1);
  const metCount = targets.filter((t) => t.status === "met").length;
  const theme = useTheme();
  const masteryChartColor =
    getStatusStyles(theme)[goal.status as keyof ReturnType<typeof getStatusStyles>]?.dotColor;

  return (
    <Stack
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "12px",
        p: 5,
        gap: 4,
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ gap: 3 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={500}>
            {goal.title}
          </Typography>
          {(goal.status === "discontinued" || goal.status === "on-hold") && goal.statusRationale ? (
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600 }}>
                Reason:
              </Box>{" "}
              {goal.statusRationale}
            </Typography>
          ) : (
            goal.description && (
              <Typography variant="body2" color="text.secondary">
                {goal.description}
              </Typography>
            )
          )}
        </Box>
        {caption && (
          <Typography
            variant="caption"
            color="text.secondary"
            fontStyle="italic"
            sx={{ flexShrink: 0 }}
          >
            {caption}
          </Typography>
        )}
      </Stack>

      {showChart && (
        <Stack sx={{ gap: 3 }}>
          <Typography variant="body2" fontWeight={500}>
            Progress Visualization
          </Typography>
          <Stack direction="row" gap={2}>
            <Button
              variant="contained"
              size="medium"
              color={chartMode === "cumulative" ? "primary" : "secondary"}
              startIcon={<ChartBar size={20} />}
              onClick={() => setChartMode("cumulative")}
            >
              Cumulative Record
            </Button>
            <Button
              variant="contained"
              size="medium"
              color={chartMode === "individual" ? "primary" : "secondary"}
              startIcon={<TargetIcon size={20} />}
              onClick={() => setChartMode("individual")}
            >
              Individual Target Progress
            </Button>
          </Stack>
          {chartMode === "cumulative" ? (
            <Stack sx={{ p: "16px", borderRadius: "12px", bgcolor: "grey.50" }}>
              {targets.length === 0 ? (
                <ChartEmptyState
                  minHeight={280}
                  title="Nothing to chart."
                  description="Add target and session data to render mastery chart."
                />
              ) : sessionHistoryState === "no" ? (
                <ChartEmptyState
                  minHeight={280}
                  title="Nothing to chart."
                  description="No session history yet for this goal."
                />
              ) : isMasteryHistoryLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
                  <CircularProgress aria-label="Loading mastery history" />
                </Box>
              ) : errorMasteryHistory ? (
                <ChartErrorState minHeight={280} onRetry={() => refetchMasteryHistory()} />
              ) : cumulativeData.length === 0 ? (
                <ChartEmptyState
                  minHeight={280}
                  description="Mastery history will chart here once this goal's targets start reaching mastery."
                />
              ) : (
                <GoalCumulativeRecordChart
                  labels={cumulativeLabels}
                  data={cumulativeData}
                  totalTargets={totalTargetsForChart}
                  masteredSummary={`${metCount} of ${totalTargets} targets mastered`}
                  color={masteryChartColor}
                  onChartReady={(chart) => registerChart(goal.id, "cumulative", null, chart)}
                />
              )}
            </Stack>
          ) : (
            <Stack sx={{ gap: 1, p: 2, borderRadius: "8px", bgcolor: "grey.50" }}>
              {targets.map((target) => (
                <TargetProgressBlock
                  key={target.id}
                  target={target}
                  behaviorSubject={
                    typeof goal.behaviorSubject === "string" ? goal.behaviorSubject : undefined
                  }
                  isIncluded={selectedTargetIds[target.id] !== false}
                  onToggle={(selected) => onTargetSelectionChange(target.id, selected)}
                  onChartReady={(chart) => registerChart(goal.id, "target", target.id, chart)}
                />
              ))}
            </Stack>
          )}
        </Stack>
      )}

      {showProgressStatement && (
        <Stack sx={{ gap: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            Progress Statement
          </Typography>
          <TextField
            multiline
            minRows={2}
            placeholder="Describe the client's progress on this goal..."
            value={progressStatement}
            onChange={(e) => onProgressStatementChange(e.target.value)}
            fullWidth
          />
        </Stack>
      )}
    </Stack>
  );
};

export default GoalProgressCard;
